import { ActiveToast } from 'ngx-toastr';
import { Observable } from 'rxjs';

import { Injectable } from '@angular/core';

// Import types from the CAD service
import type {
  CadFeatureData,
  CadListUpdatePayload,
  CadFilterConfig,
  CadMarkerLayer,
  CameraGeoCount,
  CadActionType,
  CadAttributeType,
} from '@app/dashboard/map/services/map-cad/types';
import type { SnLayer, SnEsriQueryLayer, SnLayerSetting } from '@lib/api/entities';
import type { SnCad } from '@lib/cad';
import type { GoogleMapLayer } from '@lib/geo/google/map';

import { IFsMap } from '../types';

export interface ISnCadToastData {
  cad: SnCad;
  layer: SnLayer;
  feature: CadFeatureData;
}

@Injectable()
export abstract class FsMapCadServiceProvider<TMap extends IFsMap> {
  public abstract get cadFields$(): Observable<{ fields: string[]; layer: SnEsriQueryLayer }>;
  public abstract get cadList$(): Observable<SnCad[]>;
  public abstract get cadName$(): Observable<string>;
  public abstract get cadActive$(): Observable<string[]>;
  public abstract get cadToast$(): Observable<ISnCadToastData>;
  public abstract get layerSettings(): SnLayerSetting;
  public abstract get addCad$(): Observable<SnCad>;
  public abstract get cameraCounts$(): Observable<CameraGeoCount[]>;
  public abstract get updateWebGlMarkers$(): Observable<Record<string, any>>;
  public abstract get initAndExpandChange$(): Observable<void>;
  public abstract get layerAdded$(): Observable<SnLayer>;
  public abstract get layerRemoved$(): Observable<SnLayer>;
  public abstract get skydioUpdated$(): Observable<CadFeatureData>;

  public abstract activeCad: SnCad;
  public abstract cadActive: string;
  public abstract currentSize: number;
  public abstract cadFilters: CadFilterConfig;
  public abstract adminCadFilters: CadFilterConfig;
  public abstract _fields: string[];
  public abstract rapidSOSEnabled: boolean;
  public abstract mobilePhone: boolean;
  public abstract cadMarkers: CadMarkerLayer[];
  public abstract webGlMarkers: Record<string, any>;
  public abstract activeLayers: SnLayer[];
  public abstract cadDrawerLayerNames: string;
  public abstract combinedCad: Map<string, CadFeatureData[]>;
  public abstract isExpanded: boolean;
  public abstract cadCameras: Map<string, number>;

  public abstract initAndExpandChange(): void;
  public abstract enableRapidSOS(): void;
  public abstract disableRapidSOS(): void;
  public abstract updateCadList(layer: SnLayer, payload: CadListUpdatePayload): void;
  public abstract setActive(active: string): void;
  public abstract addActiveLayer(layer: SnLayer): void;
  public abstract setAvl(mapLayer: unknown): void;
  public abstract setFields(fields: string[], layer?: SnEsriQueryLayer): void;
  public abstract removeIdleCad(seconds: number): void;
  public abstract updateCadById(
    id: string | Number,
    item: CadFeatureData,
    translate?: boolean,
  ): void;
  public abstract removeCadById(id: string | Number): void;
  public abstract initPlayback(data: CadFeatureData, attributeType: CadAttributeType): boolean;
  public abstract initCadToast(
    data: CadFeatureData,
    layer?: SnLayer,
    attributeType?: CadAttributeType,
  ): ActiveToast<any>;
  public abstract initCadSound(data?: CadFeatureData, attributeType?: CadAttributeType): void;
  public abstract showDynamicCadToast(cad: SnCad): void;
  public abstract getNearbyCameras(data: SnCad, radius: number): void;
  public abstract closeCadToasts(): void;
  public abstract checkPhoneNumber(phoneNumber: string): void;
  public abstract checkDispatch(): void;
  public abstract updateWebGlMarkers(data: Record<string, any>): void;
  public abstract removeActiveLayer(layer: SnLayer): void;
  public abstract dockCamera(feature: CadFeatureData): void;
  public abstract onFeatureMouseOver(feature: CadFeatureData): void;
  public abstract onFeatureMouseOut(feature: CadFeatureData): void;
  public abstract skydioUpdate(feature: CadFeatureData): void;
  public abstract coreMobileUpdate(feature: CadFeatureData): void;
  public abstract ngOnDestroy(): void;
}
